import type { ApiResponse, GenerateResponse, SiteConfigResponse, VoiceGender } from "@narratenow/shared";

/** One finished listening session, as reported to the API. */
export interface PlayReport {
  siteId: string;
  articleId: string;
  sessionId: string;
  listenSeconds: number;
  durationSeconds?: number;
  completed: boolean;
}

/**
 * Everything the widget asks of the network. The real client implements it; the
 * dashboard preview passes a stand-in that never leaves the page.
 */
export interface WidgetBackend {
  fetchConfig(siteId: string): Promise<SiteConfigResponse>;
  generate(
    siteId: string,
    articleId: string,
    content: string,
    voice?: VoiceGender,
    signal?: AbortSignal,
  ): Promise<GenerateResponse>;
  reportPlay(payload: PlayReport): void;
}

export class NarrateNowClient implements WidgetBackend {
  private baseUrl: string;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl.replace(/\/+$/, "");
  }

  async fetchConfig(siteId: string): Promise<SiteConfigResponse> {
    const url = `${this.baseUrl}/v1/site/config`;

    const res = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ siteId }),
    });

    const body = (await res.json()) as ApiResponse<SiteConfigResponse>;

    // Thrown rather than answered with free-plan defaults: the widget may already
    // hold a cached copy of the real config, and a made-up one would overwrite it.
    // The caller keeps whatever it has — cached values, or the free-plan defaults.
    if (!res.ok || !body.success || !body.data) {
      throw new Error("Could not load site config.");
    }

    return body.data;
  }

  /**
   * Report one finished listening session.
   *
   * Deliberately a single call per session rather than a progress ping: a
   * 15-second tick would be ~14 requests per play for data that fits in one, and
   * the extra rows buy nothing the accumulated total does not already carry.
   *
   * Uses sendBeacon so the report survives the page unload that usually triggers
   * it — a normal fetch is cancelled when the document goes away. Never throws:
   * analytics must not be able to break playback.
   */
  reportPlay(payload: PlayReport): void {
    const url = `${this.baseUrl}/v1/events`;

    try {
      const body = JSON.stringify(payload);

      if (typeof navigator !== "undefined" && typeof navigator.sendBeacon === "function") {
        // text/plain avoids a CORS preflight, which would not survive unload.
        navigator.sendBeacon(url, new Blob([body], { type: "text/plain;charset=UTF-8" }));
        return;
      }

      void fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body,
        keepalive: true,
      }).catch(() => {});
    } catch {
      // Ignore — a dropped analytics event is not worth surfacing.
    }
  }

  async generate(
    siteId: string,
    articleId: string,
    content: string,
    voice: VoiceGender = "male",
    signal?: AbortSignal,
  ): Promise<GenerateResponse> {
    const url = `${this.baseUrl}/v1/generate`;

    const res = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ siteId, articleId, content, voice }),
      signal,
    });

    const body = (await res.json()) as ApiResponse<GenerateResponse>;

    if (!res.ok || !body.success || !body.data) {
      throw new Error("An error occurred. Contact the site admin.");
    }

    return body.data;
  }
}
